import { logger } from "./Logger";
import { mailbotsAdminBrowser } from "./utils";

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

// Conversions are fired once per browser session. Without this, a user who
// refreshes /skills after signing up would report a second StartTrial.
const FIRED_KEY = "futTrackedConversions";

const alreadyFired = eventName => {
  try {
    const fired = JSON.parse(window.sessionStorage.getItem(FIRED_KEY) || "[]");
    return fired.includes(eventName);
  } catch (e) {
    return false;
  }
};

const markFired = eventName => {
  try {
    const fired = JSON.parse(window.sessionStorage.getItem(FIRED_KEY) || "[]");
    window.sessionStorage.setItem(
      FIRED_KEY,
      JSON.stringify(fired.concat(eventName))
    );
  } catch (e) {
    // sessionStorage unavailable (private mode, blocked cookies). The event
    // still fires — we just lose de-duplication.
  }
};

/**
 * Load the Meta Pixel. Called once on app boot from index.js.
 *
 * The www marketing site loads its pixel through GTM (container GTM-PDJ3VK),
 * but the web app has no GTM container, so the pixel is loaded directly here.
 * Both use the same pixel ID, and fbevents.js scopes its _fbp / _fbc cookies
 * to followupthen.com, so a click landing on www carries its attribution
 * across to app.followupthen.com without threading fbclid through the URL.
 */
export const initMetaPixel = () => {
  if (!PIXEL_ID) {
    logger.log("initMetaPixel: no REACT_APP_META_PIXEL_ID set", {
      level: "warn"
    });
    return;
  }
  if (typeof window === "undefined" || window.fbq) return;

  /* eslint-disable */
  !(function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );
  /* eslint-enable */

  window.fbq("init", PIXEL_ID);
  window.fbq("track", "PageView");
};

// Each conversion lists what to send to each destination. To report a
// conversion to another ad platform, add its key here and a sender below.
//   meta:   standard Meta Pixel event name, fired in the browser
//   server: event sent to our API, which forwards it to platforms that need a
//           secret key (OpenAI Ads). Fields follow the OpenAI events API.
const CONVERSIONS = {
  signup: {
    meta: "CompleteRegistration"
  },
  trial_started: {
    meta: "StartTrial",
    server: { type: "trial_started", data: { type: "plan_enrollment" } }
  }
};

const newEventId = () =>
  window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;

const SENDERS = {
  meta: (eventName, params) => {
    if (typeof window.fbq !== "function") {
      logger.log(`trackConversion: pixel unavailable for ${eventName}`, {
        level: "warn"
      });
      return;
    }
    window.fbq("track", eventName, params);
  },

  server: event => {
    mailbotsAdminBrowser
      .sendConversionEvent({
        id: newEventId(),
        timestamp_ms: Date.now(),
        source_url: window.location.href,
        action_source: "web",
        ...event
      })
      .catch(e =>
        logger.log(`trackConversion: API send did not succeed for ${event.type}`, {
          level: "warn",
          data: { message: e.message }
        })
      );
  }
};

/**
 * Report a conversion to every destination listed for it in CONVERSIONS
 * (and to GTM's dataLayer if a container is ever added to this app —
 * sendGtmEvent in utils.js is currently a no-op here).
 *
 * @param {string} name Key in CONVERSIONS, e.g. "trial_started"
 * @param {object} params Optional Meta event params (value, currency, etc.)
 * @param {object} options { once: false } to allow repeat fires in a session
 */
export const trackConversion = (name, params = {}, options = {}) => {
  const { once = true } = options;
  const conversion = CONVERSIONS[name];
  if (!conversion) {
    logger.log(`trackConversion: unknown conversion ${name}`, {
      level: "warn"
    });
    return;
  }
  if (typeof window === "undefined") return;
  if (once && alreadyFired(name)) return;

  Object.keys(conversion).forEach(destination => {
    try {
      SENDERS[destination](conversion[destination], params);
    } catch (e) {
      logger.log(`trackConversion: ${destination} did not succeed for ${name}`, {
        level: "warn",
        data: { message: e.message }
      });
    }
  });
  if (once) markFired(name);
  logger.log(`conversion tracked: ${name}`, { data: params });

  if (window.dataLayer) {
    window.dataLayer.push({ event: name, ...params });
  }
};
